import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { DateTime } from 'luxon';

import historicalData from '../../api/historical';
// eslint-disable-next-line import/no-cycle
import { AppThunk, RootState } from '../../store';

const processorSlice = createSlice({
  name: 'processor',
  initialState: {
    stats: {
      currentPrice: 0,
      btcAmount: 0,
      fiatValue: 0,
      fiatSpent: 0,
      percentGain: 0,
      fiatGain: 0,
      status: 'idle',
      error: null,
    },
    graphData: {
      btc: [],
      fiat: [],
    },
  },
  reducers: {
    updateStats: (state, action) => {
      const stats = action.payload;
      state.stats.currentPrice = roundToDecimal(stats.currentPrice);
      state.stats.btcAmount = roundToDecimal(stats.btcAmount);
      state.stats.fiatValue = roundToDecimal(stats.fiatValue);
      state.stats.fiatSpent = roundToDecimal(stats.fiatSpent);
      state.stats.percentGain = roundToDecimal(stats.percentGain);
      state.stats.fiatGain = roundToDecimal(stats.fiatGain);
      state.stats.status = 'success';

      const currentDate = new Date();
      state.stats.lastUpdate = `${currentDate.toLocaleTimeString()}`;
    },
    updateGraphData: (state, action) => {
      state.graphData.btc = action.payload.btc;
      state.graphData.fiat = action.payload.fiat;
    },
  },
});

const roundToDecimal = (numString) => {
  return Math.round(parseFloat(numString) * 100) / 100;
};

const calculateStats = (transactions, currentPrice) => {
  let btcAmount = 0;
  let fiatValue = 0;
  let fiatSpent = 0;
  let percentGain = 0;
  let fiatGain = 0;
  let totalFeesPaid = 0;

  transactions.forEach(row => {
    if (row) {
      btcAmount += row.btcAmount;
      fiatSpent += row.fiatAmount;
    }
  });

  fiatValue = currentPrice * btcAmount;
  fiatGain = fiatValue - fiatSpent;
  percentGain = (fiatGain / fiatSpent) * 100;

  return {
    currentPrice,
    btcAmount,
    fiatValue,
    fiatSpent,
    percentGain,
    fiatGain,
  };
};

const prepareActive = (state) => {
  const { exchanges, upload } = state;
  const activeTransactions = [];

  exchanges.forEach((exchange) => {
    if (exchange.active) {
      upload[exchange.name].forEach((el) => {
        activeTransactions.push(el);
      });
    }
  });

  return activeTransactions;
};

export const {
         updatePrice,
         updateStats,
         updateGraphData,
         updateStatsStatus,
       } = processorSlice.actions;

export const fetchStats = (transactions): AppThunk => (dispatch) => {
  return axios.get(`https://api.coindesk.com/v1/bpi/currentprice.json`)
    .then(res => {
      const currentPrice = JSON.stringify(
        res.data['bpi']['USD']['rate_float']
      );

      const stats = calculateStats(transactions, currentPrice);
      dispatch(updateStats(stats));
  });
};

const getDifferenceInDays = (date1, date2) => {
  const diffTime = Math.abs(new Date(date2) - new Date(date1));
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const getNextDay = (date) => {
  return DateTime.fromISO(date).plus({ days: 1 }).toFormat('yyyy-MM-dd');
};

export const prepareGraphData = (transactions): AppThunk => (dispatch) => {
  const graphData = [];
  let runningTotal = 0;

  transactions.sort(function(a, b) {
    var keyA = new Date(a.date),
      keyB = new Date(b.date);
    // Compare the 2 dates
    if (keyA < keyB) return -1;
    if (keyA > keyB) return 1;
    return 0;
  });

  transactions.forEach((transaction) => {
    const prev = graphData[graphData.length - 1];

    // if (!dateValues[transaction.date]) {
    //   dateValues[transaction.date] = {
    //     btcAmount: 0,
    //     fiatAmount: 0,
    //   };
    // }

    // dateValues[transaction.date].btcAmount += transaction.btcAmount;
    // dateValues[transaction.date].fiatAmount += transaction.fiatAmount;

    // Handle days without purchases.
    const differenceInDays = prev
      ? getDifferenceInDays(prev[0], transaction.date)
      : 0;
    if (prev && prev[0] != transaction.date && differenceInDays > 1) {
      let current = prev[0];
      for (let index = 1; index < differenceInDays; index++) {
        const nextDay = getNextDay(current);
        graphData.push([nextDay, runningTotal]);
        current = nextDay;
      }
    }

    // Handle multiple purchases on the same day.
    if (prev && prev[0] == transaction.date) {
      prev[1] += roundToDecimal(transaction.btcAmount);
    } else {
      graphData.push([
        transaction.date,
        roundToDecimal(transaction.btcAmount + runningTotal),
      ]);
    }

    runningTotal += transaction.btcAmount;
  });

  const usdGraphData = graphData.map((day) => {
    return [day[0], roundToDecimal(historicalData[day[0]] * day[1])];
  });

  dispatch(updateGraphData({ btc: graphData, fiat: usdGraphData }));
};

export default processorSlice.reducer;

export const selectStatsStatus = (state: RootState) =>
         state.processor.stats.status;

export const selectStats = (state: RootState) => state.processor.stats;
export const selectBTCGraphData = (state: RootState) => state.processor.graphData.btc;
export const selectFiatGraphData = (state: RootState) =>
  state.processor.graphData.fiat;


export const selectActiveTransactions = (state: RootState) =>
  prepareActive(state);



